import Http from "../http";

// `settings` is optional on a question and often simply absent (3 of the 4
// questions on the E2E job type have no such field). Consumers assume it is
// there: QuestionFormRenderer reads `question.settings.withBackground` in its
// *constructor*, and the question type components read
// `question.settings.isMandatory` — so one settings-less question threw before
// anything rendered and the error boundary sent /job_type/editor/:id to
// /error/500. Confirmed live on pink 2026-08-23.
//
// Same call as services/api/jobs.js makes for jobs: guarantee the shape at the
// funnel every question read comes through, rather than guarding each of the
// dozens of `question.settings.X` sites.
const withQuestionSettings = (question) =>
  question && !question.settings ? { ...question, settings: {} } : question;

export const normalizeQuestions = (data) => {
  if (Array.isArray(data)) return data.map(withQuestionSettings);
  if (data && Array.isArray(data.docs))
    return { ...data, docs: data.docs.map(withQuestionSettings) };
  if (data && Array.isArray(data.data))
    return { ...data, data: data.data.map(withQuestionSettings) };
  return withQuestionSettings(data);
};

export const listQuestionsWithFilters = async (params) => {
  const path = `/v2/question`;
  try {
    const response = await Http.sharedInstance.withAuth().get(path, {
      params: params,
    });
    return normalizeQuestions(response.data);
  } catch (e) {
    return {
      success: false,
      data: {},
    };
  }
};

export const listQuestionProjects = async (params) => {
  const path = `/v2/question/projects`;
  try {
    const response = await Http.sharedInstance.withAuth().get(path, {
      params: params,
    });
    return response.data;
  } catch (e) {
    return {
      success: false,
      data: {},
    };
  }
};

export const listQuestions = async (params) => {
  const path = `/v2/jobtype/${params.id}/questions`;
  try {
    const response = await Http.sharedInstance.withAuth().get(path);
    return normalizeQuestions(response.data);
  } catch (e) {
    return {
      success: false,
      data: {},
    };
  }
};

export const getQuestionAnswers = async (idQuestion, idJob) => {
  const path = `/v2/questions/${idQuestion}/answers?id_job=${idJob}`;
  try {
    const response = await Http.sharedInstance.withAuth().get(path);
    return response.data;
  } catch (e) {
    return [];
  }
};

export const listGlobalQuestions = async (jobTypes) => {
  const path = `/v2/question`;
  const params = {
    only_global: true,
    jobType: jobTypes,
  };
  try {
    const response = await Http.sharedInstance.withAuth().get(path, {
      params,
    });
    return normalizeQuestions(response.data);
  } catch (e) {
    return {
      success: false,
      data: {},
    };
  }
};

export const createQuestion = async (params) => {
  const path = `/v2/question`;
  try {
    const response = await Http.sharedInstance.withAuth().post(path, {
      title: params.title,
      description: params.description,
      is_global: params.isGlobal,
      settings: params.settings,
      jobType: params.jobType,
      type: params.type,
    });
    return response.data;
  } catch (e) {
    return {
      success: false,
      data: {},
    };
  }
};

export const reorderQuestions = async (params) => {
  const path = `/v2/jobtype/${params.id}/reorder-questions`;
  try {
    const response = await Http.sharedInstance.withAuth().put(path, {
      questions: params.questions,
    });

    return response.data;
  } catch (e) {
    console.error(e);
    return null;
  }
};

export const updateQuestion = async (params) => {
  const path = `/v2/question/${params.ID}`;
  try {
    const response = await Http.sharedInstance.withAuth().put(path, {
      title: params.title,
      description: params.description,
      is_global: params.isGlobal,
      settings: params.settings,
      job_type: params.jobType,
      type: params.type,
      sequence: params.sequence,
      root: params.root ? params.root : null,
      disabled: params.disabled,
      withBackground: params.withBackground,
      backgroundDesktop: params.backgroundDesktop,
      backgroundMobile: params.backgroundMobile,
      darkMode: params.darkMode,
      isClientVisible: params.isClientVisible,
      fileRequired: params.fileRequired,
      stepIcon: params.stepIcon,
    });
    return response.data;
  } catch (e) {
    return {
      success: false,
      data: {},
    };
  }
};

export const deleteQuestion = async (params) => {
  const path = `/v2/question/${params.id}`;
  try {
    const response = await Http.sharedInstance.withAuth().delete(path);
    return response.data;
  } catch (e) {
    return {
      success: false,
      data: {},
    };
  }
};
